/** Tiny Web Audio synth for chat notification sounds.
 *
 *  Why generate instead of shipping an mp3:
 *  - One ~150-line file vs a binary asset + MIME plumbing.
 *  - Volume + pitch are tweakable in code; A/B-ing a different
 *    "ding" is a one-line change.
 *  - No asset cache invalidation surprises across deploys.
 *
 *  Two voices:
 *  - ``playMessageSound()`` — gentle two-note ascending ding for
 *    ordinary new_message notifications.
 *  - ``playMentionSound()`` — brighter, three-note arpeggio for
 *    @mentions; meant to feel a little more "you".
 *
 *  Browser-autoplay rules require a user gesture before AudioContext
 *  can play. We lazily create the context on the first sound request
 *  and additionally hook a one-shot ``mousedown`` to resume any
 *  context that started in ``suspended`` state — that covers the
 *  "user just loaded the page and a notif arrived" race.
 */

let _ctx: AudioContext | null = null;
let _listenersBound = false;

function ensureContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  // Some Safari versions only expose webkitAudioContext.
  type AC = typeof AudioContext;
  const Ctor: AC | undefined =
    (window as unknown as { AudioContext?: AC }).AudioContext ||
    (window as unknown as { webkitAudioContext?: AC }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
  } catch {
    return null;
  }

  if (!_listenersBound && typeof document !== "undefined") {
    _listenersBound = true;
    // First user gesture resumes the context if Chrome / Safari
    // suspended it. The listener is one-shot via { once: true }.
    const wake = () => {
      if (_ctx && _ctx.state === "suspended") {
        _ctx.resume().catch(() => undefined);
      }
    };
    document.addEventListener("pointerdown", wake, { once: true });
    document.addEventListener("keydown", wake, { once: true });
  }
  return _ctx;
}

interface NoteSpec {
  /** Hz */
  freq: number;
  /** Seconds, relative to the playback start */
  start: number;
  /** Seconds. The envelope is attack-decay only; a note "lasts" for
   *  ``duration`` and then drops to silence. */
  duration: number;
  /** Peak gain on the envelope. Stays well under 1 to avoid clipping
   *  when the OS mixer adds its own headroom. */
  gain: number;
  /** "sine" feels warm + chime-like; "triangle" is slightly brighter
   *  and reads better for the mention voice. */
  type?: OscillatorType;
}

function playSequence(notes: NoteSpec[]): void {
  const ctx = ensureContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  // A shared bus → master gain → destination so we can dip the whole
  // pattern with one node if we ever add a "low volume" preference.
  const master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);

  for (const n of notes) {
    const osc = ctx.createOscillator();
    osc.type = n.type ?? "sine";
    osc.frequency.value = n.freq;
    const env = ctx.createGain();
    // Linear attack → exponential decay. Exponential decay sounds
    // perceptually natural; a linear ramp to zero clicks at the tail.
    const start = now + n.start;
    const peak = start + 0.015; // 15 ms attack
    const end = start + n.duration;
    env.gain.setValueAtTime(0.0001, start);
    env.gain.exponentialRampToValueAtTime(n.gain, peak);
    env.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(env).connect(master);
    osc.start(start);
    osc.stop(end + 0.05);
  }
}

/** Default voice — soft, friendly two-note ding (E5 → A5). */
export function playMessageSound(): void {
  playSequence([
    { freq: 659.25, start: 0, duration: 0.18, gain: 0.18, type: "sine" }, // E5
    { freq: 880.0, start: 0.09, duration: 0.22, gain: 0.18, type: "sine" }, // A5
  ]);
}

/** Mention voice — three-note ascending arpeggio (A5 → C#6 → E6),
 *  slightly louder and on a triangle wave so it cuts through the
 *  ambient room a bit more. Still well under "alarm" territory. */
export function playMentionSound(): void {
  playSequence([
    { freq: 880.0, start: 0, duration: 0.16, gain: 0.2, type: "triangle" }, // A5
    { freq: 1108.73, start: 0.08, duration: 0.16, gain: 0.2, type: "triangle" }, // C#6
    { freq: 1318.51, start: 0.16, duration: 0.22, gain: 0.22, type: "triangle" }, // E6
  ]);
}
